/**
 * Reines Render-/Sanitize-Modul für den Join/Leave-Announcer (Slice 1).
 *
 * DOM-/DB-/Netz-frei → sowohl serverseitig (Action/Worker) als auch im UI-Bundle
 * (Live-Preview) importierbar und voll testbar. Port der Farb-/Fallback-Logik aus
 * `worker/stats.ts` (chatlog.php `announceLines`), aber template-basiert.
 *
 * Farbe: bei `colorEnabled` wird der EINGESETZTE Variablenwert mit
 * `|c1001…|c1001` (orange) umschlossen — wie im alten Verhalten NICHT die ganze
 * Zeile, nur die Werte. Literaltext im Template bleibt ungefärbt.
 */

export interface JoinAnnouncerSettings {
  joinEnabled: boolean;
  leaveEnabled: boolean;
  statsEnabled: boolean;
  colorEnabled: boolean;
  cooldownSeconds: number;
  joinLine1: string;
  joinLine2: string;
  joinLine3: string;
  leaveTemplate: string;
}

/** Variablenwerte für ein Render. Fehlende/leere Werte → sinnvoller Default. */
export type AnnouncerVars = Record<string, string | number | null | undefined>;

/** Maximale Zeilenlänge fürs RCON `game.sayAll` (inkl. Farbcodes), defensiv. */
export const MAX_LINE_LEN = 150;

/** Default-Templates = heutiges hardcoded Verhalten (kein Regress). */
export const DEFAULT_JOIN_ANNOUNCER: JoinAnnouncerSettings = {
  joinEnabled: true,
  // Leave default AUS: ModManager/Game (und ggf. das Legacy-PHP) melden Leaves
  // bereits → kein zusätzlicher Chat-Spam aus unserem Tool. Join (mit Stats)
  // bleibt der Mehrwert. Admins können Leave pro Server bewusst einschalten.
  leaveEnabled: false,
  statsEnabled: true,
  colorEnabled: true,
  cooldownSeconds: 60,
  joinLine1: ">> {name} connected.",
  joinLine2: "Rank: {rank} ({rankName}) - Playtime: {playtimeHours}h - K/D: {kd} - Score: {score}",
  joinLine3: "Fav Class: {bestKit} - Fav Vehicle: {bestVehicle} - Playstyle: {playstyle}",
  leaveTemplate: "{name} left the battlefield",
};

/** Unterstützte Template-Variablen + Default, wenn der Wert fehlt/leer ist. */
export const ANNOUNCER_VARIABLES: ReadonlyArray<{ name: string; default: string; optional?: boolean }> = [
  { name: "name", default: "Player" },
  { name: "displayName", default: "Player" },
  { name: "pid", default: "0" },
  { name: "clanTag", default: "—" },
  { name: "rank", default: "0" },
  { name: "rankName", default: "Unknown" },
  { name: "playtimeHours", default: "0" },
  { name: "kd", default: "0.00" },
  { name: "score", default: "0" },
  { name: "bestKit", default: "Unknown" },
  { name: "bestVehicle", default: "—" },
  { name: "playstyle", default: "Unknown" },
  { name: "country", default: "Unknown", optional: true },
  { name: "city", default: "—", optional: true },
  { name: "asn", default: "—", optional: true },
];

const VAR_DEFAULTS: Record<string, string> = Object.fromEntries(
  ANNOUNCER_VARIABLES.map((v) => [v.name, v.default]),
);

/** Beispiel-Spieler für die Preview (Slice 1: kein Live-Spieler nötig). */
export const SAMPLE_ANNOUNCER_VARS: AnnouncerVars = {
  name: "RAiN^",
  displayName: "AfeX^ RAiN^",
  pid: "118305072",
  clanTag: "AfeX^",
  rank: "39",
  rankName: "Field Marshal Gold",
  playtimeHours: "412.5",
  kd: "2.13",
  score: "184320",
  bestKit: "Assault",
  bestVehicle: "Gunship",
  playstyle: "Squad Leader",
  country: "Germany",
  city: "Koblenz",
  asn: "AS3320",
};

/**
 * Macht einen gerenderten String RCON-sicher: Quotes raus (sonst `sayAll "…"`
 * Breakout), Zeilenumbrüche/Tabs/Control-Chars → Space, Whitespace kollabieren,
 * trimmen, harte Längenbegrenzung. Farbcodes `|c1001` bleiben erhalten.
 */
export function sanitizeRconText(s: string): string {
  let out = String(s ?? "")
    .replace(/[\r\n\t]+/g, " ")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/"/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  if (out.length > MAX_LINE_LEN) out = out.slice(0, MAX_LINE_LEN).trim();
  return out;
}

const VAR_RE = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

/**
 * Rendert EIN Template: ersetzt bekannte `{var}` durch den Wert (oder Default),
 * färbt den Wert bei `color`, lässt unbekannte `{var}` roh stehen (kein Crash),
 * und sanitized das Ergebnis. Leeres/whitespace-only Template → "".
 */
export function renderTemplate(tpl: string, vars: AnnouncerVars, color: boolean): string {
  const replaced = String(tpl ?? "").replace(VAR_RE, (whole, varName: string) => {
    const def = VAR_DEFAULTS[varName];
    if (def === undefined) return whole; // unbekannte Variable: unverändert lassen
    const raw = vars[varName];
    const val = raw === undefined || raw === null || String(raw).trim() === "" ? def : String(raw);
    return color ? `|c1001${val}|c1001` : val;
  });
  return sanitizeRconText(replaced);
}

/**
 * Join-Zeilen rendern. Zeile 1 ist die Basis-Join-Zeile; Zeile 2+3 sind die
 * Stats-Zeilen und kommen NUR, wenn Stats aktiv UND (zur Laufzeit) verfügbar
 * sind — sonst „nur einfache Join-Zeile" (Fallback). Leere Zeilen werden
 * übersprungen (kein Spam).
 */
export function renderJoinLines(
  settings: JoinAnnouncerSettings,
  vars: AnnouncerVars,
  opts?: { statsAvailable?: boolean },
): string[] {
  const withStats = settings.statsEnabled && (opts?.statsAvailable ?? true);
  const tpls = withStats
    ? [settings.joinLine1, settings.joinLine2, settings.joinLine3]
    : [settings.joinLine1];
  return tpls.map((t) => renderTemplate(t, vars, settings.colorEnabled)).filter((l) => l !== "");
}

/** Leave-Zeile rendern; leer → "" (nichts senden). */
export function renderLeaveLine(settings: JoinAnnouncerSettings, vars: AnnouncerVars): string {
  return renderTemplate(settings.leaveTemplate, vars, settings.colorEnabled);
}

/** Klemmt/normalisiert die Settings (Cooldown 0..3600, Templates getrimmt+gekappt). */
export function normalizeSettings(input: Partial<JoinAnnouncerSettings> | null | undefined): JoinAnnouncerSettings {
  const s = input ?? {};
  const tpl = (v: unknown, dflt: string): string => {
    const t = typeof v === "string" ? v : dflt;
    return t.length > 240 ? t.slice(0, 240) : t;
  };
  const cd = Math.trunc(Number(s.cooldownSeconds));
  return {
    joinEnabled: s.joinEnabled ?? DEFAULT_JOIN_ANNOUNCER.joinEnabled,
    leaveEnabled: s.leaveEnabled ?? DEFAULT_JOIN_ANNOUNCER.leaveEnabled,
    statsEnabled: s.statsEnabled ?? DEFAULT_JOIN_ANNOUNCER.statsEnabled,
    colorEnabled: s.colorEnabled ?? DEFAULT_JOIN_ANNOUNCER.colorEnabled,
    cooldownSeconds: Number.isFinite(cd) ? Math.min(3600, Math.max(0, cd)) : DEFAULT_JOIN_ANNOUNCER.cooldownSeconds,
    joinLine1: tpl(s.joinLine1, DEFAULT_JOIN_ANNOUNCER.joinLine1),
    joinLine2: tpl(s.joinLine2, DEFAULT_JOIN_ANNOUNCER.joinLine2),
    joinLine3: tpl(s.joinLine3, DEFAULT_JOIN_ANNOUNCER.joinLine3),
    leaveTemplate: tpl(s.leaveTemplate, DEFAULT_JOIN_ANNOUNCER.leaveTemplate),
  };
}

/** Komplette Preview (Join + Leave) für gegebene Settings + Beispiel-Vars. */
export function previewAnnouncer(
  settings: JoinAnnouncerSettings,
  vars: AnnouncerVars = SAMPLE_ANNOUNCER_VARS,
): { joinLines: string[]; joinLinesNoStats: string[]; leaveLine: string } {
  return {
    joinLines: renderJoinLines(settings, vars, { statsAvailable: true }),
    joinLinesNoStats: renderJoinLines(settings, vars, { statsAvailable: false }),
    leaveLine: renderLeaveLine(settings, vars),
  };
}
